import { getIsCardslistLoading } from "redux/SingleDataset/selectors";
import ImageWithFallback from "views/components/ImageWithFallback";
import VideoIndicationIcon from "views/uikit/VideoIndicationIcon";
import ScrollAwareHover from "views/components/ScrollAwareHover";
import { CardBodyImage, OnUpdateAssetData, RelevanceType } from "types";
import useHoverDelay from "helpers/utility/utilities";
import placeholderImg from "assets/img/blank.png";
import { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import classNames from "classnames";

import styles from "./style.module.scss";
import RelevanceTypeIcon from "views/uikit/RelevanceTypeIcon";

export interface SquareImageBoxProps {
  hoverDelay?: number;
  videoURIs?: string[];
  showAssetPreview?: boolean;
  isCardslistLoading: boolean;
  plainImages: CardBodyImage[];
  thumbImages: CardBodyImage[];
  onEndContentLoading?: () => void;
  onUpdateAssetData?: OnUpdateAssetData;
}

interface SquaredImageProps {
  hoverDelay: number;
  showVideoIcon: boolean;
  showAssetPreview: boolean;
  plainImage: CardBodyImage;
  thumbImage: CardBodyImage;
  isCardslistLoading: boolean;
  relevanceType: RelevanceType | null;
  onUpdateAssetData: () => void;
  onEndContentLoading: () => void;
}

const SquaredImage = ({
  plainImage,
  thumbImage,
  hoverDelay,
  showVideoIcon,
  relevanceType,
  showAssetPreview,
  isCardslistLoading,
  onUpdateAssetData,
  onEndContentLoading,
}: SquaredImageProps) => {
  const [loading, setLoading] = useState(true);

  const showIcon = !loading && showVideoIcon;

  const tableColumnClassNames = classNames(styles.tableColumn, {
    [styles.tableColumnForAssetPreview]: showAssetPreview,
  });

  const { handleMouseEnter, handleMouseLeave } = useHoverDelay(
    onUpdateAssetData,
    hoverDelay
  );

  const handleImageLoad = () => {
    setLoading(false);
    if (isCardslistLoading && onEndContentLoading) {
      onEndContentLoading();
    }
  };

  return (
    <ScrollAwareHover
      className={tableColumnClassNames}
      onMouseOver={() => showAssetPreview && handleMouseEnter()}
      onMouseLeave={() => showAssetPreview && handleMouseLeave()}
    >
      <>
        {showIcon && <VideoIndicationIcon />}
        {relevanceType && <RelevanceTypeIcon type={relevanceType} />}
        <ImageWithFallback
          thumbImage={thumbImage}
          plainImage={plainImage}
          className={styles.singleSquareImage}
          onLoad={handleImageLoad}
        />
      </>
    </ScrollAwareHover>
  );
};

const SquareImageBox = ({
  videoURIs,
  plainImages,
  thumbImages,
  hoverDelay = 0,
  isCardslistLoading,
  showAssetPreview = false,
  onUpdateAssetData = () => {},
  onEndContentLoading = () => {},
}: SquareImageBoxProps) => {
  const getImageCount = useCallback(() => {
    if (!plainImages?.length) return 0;
    if (plainImages.length >= 16) return 4;
    if (plainImages.length >= 9) return 3;
    return 2;
  }, [plainImages]);
  const [imageCount, setImageCount] = useState(getImageCount());
  const placeholderCount = Math.max(
    0,
    imageCount * imageCount - plainImages.length
  );

  const gridStyle = () => {
    if (imageCount)
      return {
        gridTemplateColumns: `repeat(${imageCount}, minmax(1px, 1fr))`,
        gridTemplateRows: `repeat(${imageCount}, minmax(1px, 1fr))`,
      };
    return {};
  };

  const mergedStyle: any = {
    ...gridStyle(),
    visibility: !!imageCount ? "visible" : "hidden",
  };

  const dynamicUpdateAssetData = (index: number) => {
    const source = plainImages[index].src;
    onUpdateAssetData(plainImages[index].id, source);
  };

  const renderPlaceHolders = () => {
    if (placeholderCount < 1) {
      return <noscript />;
    }

    return [...Array(placeholderCount)].map((_, i) => (
      <div key={i} className={styles.tableColumn}>
        <img
          className={styles.singleSquareImage}
          data-testid={"placeholder-image"}
          src={placeholderImg}
          loading="lazy"
          alt="No Img"
        />
      </div>
    ));
  };

  useEffect(() => {
    setImageCount(getImageCount());
  }, [getImageCount]);

  return (
    <div
      className={styles.squareImagesContainer}
      data-testid={"square-images-container"}
      style={mergedStyle}
    >
      {plainImages?.length &&
        plainImages.map((plainImage: CardBodyImage, i: number) => {
          const thumbImage = thumbImages?.[i] || null;

          return (
            i < imageCount * imageCount && (
              <SquaredImage
                key={i}
                plainImage={plainImage}
                thumbImage={thumbImage}
                hoverDelay={hoverDelay}
                showAssetPreview={showAssetPreview}
                isCardslistLoading={isCardslistLoading}
                relevanceType={plainImage.relevanceType}
                showVideoIcon={!!videoURIs?.[i] || false}
                onEndContentLoading={onEndContentLoading}
                onUpdateAssetData={() => dynamicUpdateAssetData(i)}
              />
            )
          );
        })}

      {renderPlaceHolders()}
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return { isCardslistLoading: getIsCardslistLoading(state) };
};

export default connect(mapStateToProps)(SquareImageBox);
